import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { getErrorMessage } from '~shared/utils/error-handler';
import { useCreateFormConfig } from '../api/form-config-actions';
import { useGetFormTemplates } from '../api/get-form-templates';
import type { FormTemplate } from '../api/types';
import { FORM_NAME_MAX_LENGTH } from '../lib/form-validation';

interface AddFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddFormDialog({ open, onOpenChange }: AddFormDialogProps) {
  const { data: templates, isLoading } = useGetFormTemplates();
  const createMutation = useCreateFormConfig();
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(
    null,
  );
  const [formName, setFormName] = useState('');

  const reset = () => {
    setSelectedTemplate(null);
    setFormName('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleCreate = () => {
    if (!selectedTemplate) return;
    createMutation.mutate(
      {
        formType: selectedTemplate.id,
        formName: formName.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            'Form created as a draft. Turn it on from the list when ready.',
          );
          handleOpenChange(false);
        },
        onError: (mutationError) => {
          toast.error(getErrorMessage(mutationError, 'Could not create form'));
        },
      },
    );
  };

  const availableTemplates = (templates ?? []).filter((t) => t.available);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Add a form</DialogTitle>
          <DialogDescription>
            Pick a template (you can create more than one of the same type),
            then customize copy, colors, and fields.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='size-5 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <div className='max-h-[360px] space-y-2 overflow-y-auto pr-1'>
            {availableTemplates.map((template) => {
              const selected = selectedTemplate?.id === template.id;
              return (
                <button
                  key={template.id}
                  type='button'
                  onClick={() => { setSelectedTemplate(template); }}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <span className='text-sm font-medium'>{template.label}</span>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {template.description}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {selectedTemplate ? (
          <div className='space-y-1.5'>
            <Label htmlFor='new-form-name'>Form name (optional)</Label>
            <Input
              id='new-form-name'
              maxLength={FORM_NAME_MAX_LENGTH}
              placeholder={selectedTemplate.label}
              value={formName}
              onChange={(e) => { setFormName(e.target.value); }}
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => { handleOpenChange(false); }}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!selectedTemplate || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
